/**
 * GLSL the ground family's shaders share (groundMaterials.ts builds them; groundShader.ts holds the surface's own
 * declarations and albedo chunk): the map-edge haze box and the lava glow, the wind band, the surface's chunks after
 * its albedo, and the gorge walls' chunks.
 */

/**
 * GLSL shared by the surface, the cliffs and the chasm floor: the rectangular map-edge haze and the lava glow.
 * Declares nothing the three.js chunks declare.
 */
export function groundSharedGlsl(reach: number, rise: number): string {
  return `
          uniform vec4 uBounds; uniform vec3 uHaze; uniform vec2 uFog; uniform float uWindTime;
          /**
           * Distance in cells from the playable rectangle — 0 anywhere inside it, growing outward. Rectangular, not
           * radial, so the haze follows the map's own edge instead of bulging at the corners.
           */
          float beyondMap(vec2 xz) {
            vec2 lo = uBounds.xy, hi = uBounds.xy + uBounds.zw;
            vec2 d = max(lo - xz, xz - hi);
            return length(max(d, vec2(0.0)));
          }
          #ifdef GROUND_LAVA
          uniform vec4 uLava[GROUND_LAVA]; uniform vec3 uLavaCol;
          /**
           * Light the melt throws on its own basin (terrain F11): strongest down the walls, gone LavaGlowReach cells
           * out and LavaGlowRise units up. Replaces a point light per few cells.
           */
          float lavaGlow(vec3 p) {
            float g = 0.0;
            for (int i = 0; i < GROUND_LAVA; i++) {
              vec4 L = uLava[i];
              float d = length(p.xz - L.xy);
              g = max(g, (1.0 - smoothstep(L.z * 0.6, L.z + ${reach.toFixed(2)}, d)) * (1.0 - smoothstep(0.0, ${rise.toFixed(2)}, p.y - L.w)));
            }
            return g;
          }
          #endif`;
}

/**
 * The wind band (terrain F7), as GLSL. A band of gust travels down the wind at 2 units a second, one crest every
 * ~7.9 units, and a slow two-sine field gates it so it comes and goes in patches instead of marching as stripes.
 * The gate is analytic rather than value noise so the prop sway (hqMaterials) and the tree gust clips (FoliageView,
 * `windBand` in TS) evaluate exactly the same field.
 */
export function windBandGlsl(dx: number, dz: number): string {
  return `
          float windBand(vec2 xz, float t) {
            vec2 wd = vec2(${dx.toFixed(3)}, ${dz.toFixed(3)});
            float b = sin(dot(xz, wd) * 0.8 - t * 1.6);
            float gate = 0.5 + 0.5 * sin(dot(xz, vec2(-wd.y, wd.x)) * 0.19 + t * 0.11) * cos(dot(xz, wd) * 0.07 - t * 0.05);
            return smoothstep(0.55, 1.0, b) * smoothstep(0.3, 0.75, gate);
          }`;
}

// ---------------------------------------------------------------- the surface after its albedo (groundShader.ts groundMap)

/**
 * Emissive, over `#include <emissivemap_fragment>`: the ashlands' cooling cracks are LIT (the mask rides the UVs of the
 * layer it belongs to, the colour pushed past 1 for the bloom), and the lava throws light on its basin. Biomes with
 * neither pay nothing.
 */
export const groundEmissive = `
  #ifdef GROUND_EMI
  if (uEmiOn > 0.5 && gLive) {
    float ashE = texK(uEmiA, gUv * 2.3, 2.3).r;
    float roadE = gRoad > 0.0 ? texK(uEmiR, gRUv, 4.1).r : 0.0;
    totalEmissiveRadiance += uEmiColour * mix(ashE * (1.0 - gWorn * 0.7), roadE, gRoad) * (1.0 - vHaze);
  }
  #endif
  #ifdef GROUND_LAVA
  totalEmissiveRadiance += lavaGlow(vWP) * uLavaCol * (0.85 + 0.15 * sin(uWindTime * 1.6)) * (1.0 - vHaze);
  #endif
  if (gPaveGlow > 0.0) totalEmissiveRadiance += uPaveGlow.rgb * gPaveGlow * gRoad * (1.0 - vHaze);   // a paving's lit joints (groundPave.ts)`;

/**
 * After `#include <lights_fragment_end>`: baked occlusion stands in for the GTAO the phone never runs (all of it on
 * the sky light, most of it on the sun), then the cloud shadows (`cloudApply`, CloudShadows.applyGlsl).
 */
export function groundLights(cloudApply: string): string {
  return `#include <lights_fragment_end>
  reflectedLight.indirectDiffuse *= gOcc;
  reflectedLight.directDiffuse *= mix(1.0, gOcc, 0.7);
  ${cloudApply}`;
}

/** Roughness, over `#include <roughnessmap_fragment>`: per layer, from the constants in GroundMesh.Roughness. */
export function groundRoughness(r: { grass: number; dirt: number; rock: number; road: number }): string {
  return `
  float roughnessFactor = roughness * mix(mix(mix(${r.grass.toFixed(2)}, ${r.dirt.toFixed(2)}, gWorn), ${r.rock.toFixed(2)}, gSteep), ${r.road.toFixed(2)}, gRoad);`;
}

/**
 * Normal, over `#include <normal_fragment_maps>`: grass (triplanar), rock where steep, the pad's and the road's own
 * normals on the UVs the albedo already made, and the kerb's tilt. Where the haze owns the ground the smooth geometric
 * normal is all that shows.
 */
export const groundNormal = `
  if (gLive) {
    vec3 mapN = triSampleN(normalMap, gUv * 2.3, 2.3, gGrassW, gTriW);
    if (gSteep > 0.001) { vec2 kUv = gUv * 1.6; mapN = mix(mapN, triSampleN(uRockN, kUv, 1.6, dualW(kUv), gTriW), gSteep); }
    // top-down only, as the albedo; under a road skin's paving the cobble's relief is mostly flattened (the stones'
    // rims tilt the normal below instead)
    if (gRoad > 0.01) mapN = mix(mapN, mix(texK(uRoadN, gRUv, 4.1).xyz, vec3(0.5, 0.5, 1.0), step(0.5, uPave.x) * 0.8), gRoad);
    float nHard = gRoad;
    mapN = mapN * 2.0 - 1.0;
    mapN.xy *= normalScale * mix(1.0, 2.1, nHard);        // the road's relief is coarse; push its normals harder
    normal = normalize(tbn * mapN);
    if (gRoad > 0.01 && uPave.x > 0.5) normal = normalize(normal + mat3(viewMatrix) * vec3(gPaveTilt.x, 0.0, gPaveTilt.y) * 0.45 * gRoad);
    if (gKerb > 0.02) {
      // The kerb's tilt: the road's own mask as a height field ~1.5 cm tall, its gradient bending the normal outward
      // along the edge, so the lit side of the kerb catches the sun and the far side falls into shade.
      const float e = 0.035;
      vec2 grad = vec2(roadAt(vWP.xz + vec2(e, 0.0)) - roadAt(vWP.xz - vec2(e, 0.0)),
                       roadAt(vWP.xz + vec2(0.0, e)) - roadAt(vWP.xz - vec2(0.0, e))) / (2.0 * e);
      vec3 tilt = vec3(-grad.x, 0.0, -grad.y) * 0.015 * 4.0;
      normal = normalize(normal + mat3(viewMatrix) * tilt);
    }
  }`;

/** After `#include <opaque_fragment>`: the haze over the lit result. */
export const groundHaze = `
  #include <opaque_fragment>
  gl_FragColor.rgb = mix(gl_FragColor.rgb, uHaze, vHaze * 0.85);`;

// ---------------------------------------------------------------- the gorge walls (groundMaterials.ts cliffMaterial)
// Earth with rock breaking through, sedimentary banding, faceted ledges, the lava's glow from below and the mist
// pooled in the chasm: each spliced over the three.js chunk it is named for.

export function cliffCommon(shared: string, clouds: string): string {
  return `#include <common>
          varying vec3 vWP; varying float vTop; float vHaze; float cDepth;
          uniform sampler2D uSoil, uGrass;
          ${shared}
          ${clouds}
          float cnHash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
          float cnNoise(vec2 p) { vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
            return mix(mix(cnHash(i), cnHash(i + vec2(1, 0)), f.x), mix(cnHash(i + vec2(0, 1)), cnHash(i + vec2(1, 1)), f.x), f.y); }`;
}

export const CliffMapGlsl = `
          vec3 rockC = texture2D(map, vMapUv).rgb;
          vec3 soilC = texture2D(uSoil, vMapUv * 1.7 + vec2(0.31, 0.17)).rgb;
          vec3 grassC = texture2D(uGrass, vMapUv * 1.3).rgb;
          // A gorge wall is EARTH with rock breaking through it, not dressed stone. Taking the rock map as the base
          // made every wall read as masonry — a stone-lined canal. Soil is the base; rock shows in broken outcrops.
          float band = 1.0 - smoothstep(0.0, 0.34, vTop - vWP.y);
          float ragged = cnNoise(vec2(vWP.x + vWP.z, vWP.y) * 5.5);
          float outcrop = smoothstep(0.38, 0.66, cnNoise(vec2((vWP.x + vWP.z) * 0.5, vWP.y * 3.1))
                                  * 0.75 + cnNoise(vec2((vWP.x + vWP.z) * 1.9, vWP.y * 6.7)) * 0.25);
          #ifdef CLIFF_GORGE
          vec3 col = rockC;                                      // the gorge map is already earth + fractured slabs
          #else
          vec3 col = mix(soilC, rockC, outcrop * 0.8);
          #endif
          col = mix(col, soilC, smoothstep(0.15, 0.72, band) * 0.7);
          col = mix(col, grassC, smoothstep(0.74, 0.98, band + ragged * 0.18));
          // Sedimentary banding: lit band tops, dark seams, fading with depth and broken by a big soft blotch.
          cDepth = max(0.0, vTop - vWP.y);
          float depth = clamp(cDepth / 3.0, 0.0, 1.0);
          #ifndef CLIFF_GORGE
          // (not on the gorge map: horizontal bands round a trench are courses, and courses read as masonry)
          float s = cnNoise(vec2((vWP.x + vWP.z) * 0.7, vWP.y * 7.0));
          col *= 1.0 + (s - 0.5) * 0.5 * (1.0 - depth * 0.72);
          #endif
          float blotch = cnNoise(vec2(vWP.x + vWP.z * 0.6, vWP.y * 0.55) * 0.8);
          col *= 0.82 + 0.34 * blotch;
          col *= mix(0.55, 1.0, smoothstep(0.0, 1.3, band * 1.3 + 0.35));   // the deeper the face, the darker
          // warm bounce off the floor at the foot, a cooler top (terrain F8)
          col *= mix(vec3(1.0, 1.0, 1.02), vec3(1.12, 0.98, 0.84), smoothstep(1.2, 2.3, cDepth));
          diffuseColor.rgb *= col;
          vHaze = smoothstep(uFog.x, uFog.y, beyondMap(vWP.xz));`;

export const CliffNormalGlsl = `#include <normal_fragment_maps>
          // Faceted shading: half the light comes off the triangle's own plane, so the quantised ledges (buildCliffs)
          // read as chunky stacked-stone planes rather than a smooth wavy sheet.
          vec3 facetN = normalize(cross(dFdx(vViewPosition), dFdy(vViewPosition)));
          normal = normalize(mix(normal, facetN, 0.45));`;

export const CliffEmissiveGlsl = `#include <emissivemap_fragment>
          #ifdef GROUND_LAVA
          // the walls of a basin glow from below
          totalEmissiveRadiance += lavaGlow(vWP) * uLavaCol * 0.8 * (0.85 + 0.15 * sin(uWindTime * 1.6));
          #endif`;

export function cliffLights(clouds: string): string {
  return `#include <lights_fragment_end>
          ${clouds}`;
}

export const CliffHazeGlsl = `#include <opaque_fragment>
          // Height fog pooled in the chasm (lighting F9): the deeper, the more the gorge fills with cool mist, which
          // is what reads as depth from straight above. Then the map-edge haze, as on the ground.
          gl_FragColor.rgb = mix(gl_FragColor.rgb, uHaze * vec3(0.78, 0.84, 0.95), smoothstep(0.25, 2.3, cDepth) * 0.45);
          gl_FragColor.rgb = mix(gl_FragColor.rgb, uHaze, 1.0 - (1.0 - vHaze) * (1.0 - vHaze * 0.85));`;
